import AdvisorBackendService from 'AdvisorBackendService';
import BackendService from 'BackendService';
import { useCallback, useEffect, useRef, useState } from 'react';
import AIChatMessage from '../../models/AIChatMessage';
import AddressMapResult, { CustomMapBounds } from '../../models/AddressMapResult';
import { AI_ERROR_MESSAGE } from './constants';
import { useFundOpportunityChatScroll } from './useFundOpportunityChatScroll';

export interface UseFundOpportunityChatOptions {
  fundOpportunityId: string;
  advisorMode?: boolean;
}

export function useFundOpportunityChat({
  fundOpportunityId,
  advisorMode,
}: UseFundOpportunityChatOptions) {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [chatId, setChatId] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [addresses, setAddresses] = useState<AddressMapResult[]>([]);
  const [mapBounds, setMapBounds] = useState<CustomMapBounds>();
  const [showMap, setShowMap] = useState(false);

  const [hasAcceptedDisclaimer, setHasAcceptedDisclaimer] = useState(false);
  const [isLoadingDisclaimer, setIsLoadingDisclaimer] = useState(true);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);
  const [statusLoadError, setStatusLoadError] = useState(false);
  const [allowedForImpersonation, setAllowedForImpersonation] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageSender = messages.length > 0 ? messages[messages.length - 1].role : null;
  const hasMessages = messages.length > 0;

  useFundOpportunityChatScroll(chatContainerRef, messages);

  const getBackendService = useCallback((): BackendService | AdvisorBackendService => {
    return advisorMode ? new AdvisorBackendService() : new BackendService();
  }, [advisorMode]);

  const fetchDisclaimerStatus = useCallback(async () => {
    setStatusLoadError(false);
    setIsLoadingDisclaimer(true);

    try {
      const result = await getBackendService().getAiDisclaimerStatus();
      setHasAcceptedDisclaimer(result.aiChatDisclaimerAccepted);
      setAllowedForImpersonation(Boolean(result.allowedForImpersonation));
    } catch (error) {
      console.error('Failed to load AI chat disclaimer status', error);
      setStatusLoadError(true);
      setAllowedForImpersonation(false);
    } finally {
      setIsLoadingDisclaimer(false);
    }
  }, [getBackendService]);

  useEffect(() => {
    fetchDisclaimerStatus();
  }, [fetchDisclaimerStatus]);

  const handleAcceptDisclaimer = useCallback(async () => {
    if (allowedForImpersonation) {
      return;
    }

    setIsAcceptingDisclaimer(true);

    try {
      await getBackendService().acceptAiDisclaimer();
      setHasAcceptedDisclaimer(true);
    } catch (error) {
      console.error('Failed to accept AI chat disclaimer', error);
    } finally {
      setIsAcceptingDisclaimer(false);
    }
  }, [allowedForImpersonation, getBackendService]);

  const handleSendMessage = useCallback(
    async (overrideMessage?: string) => {
      if (isProcessing) {
        return;
      }

      const messageToSend = (overrideMessage ?? inputValue).trim();
      if (!messageToSend) {
        return;
      }

      const userMessage: AIChatMessage = {
        parts: [{ text: messageToSend }],
        role: 'user',
        timestamp: new Date(),
      };

      const history = [...messages, userMessage];
      setMessages(history);
      setInputValue('');
      setIsProcessing(true);

      try {
        const backendService = getBackendService();
        const aiResponse = await backendService.getAIChatResponse(
          fundOpportunityId,
          messageToSend,
          history,
          chatId,
        );

        if (aiResponse.locationData?.addresses?.length) {
          setAddresses(aiResponse.locationData.addresses as AddressMapResult[]);
          setMapBounds(aiResponse.locationData.mapBounds as CustomMapBounds);
          setShowMap(true);
        } else {
          setShowMap(false);
          setAddresses([]);
          setMapBounds(undefined);
        }

        setMessages((prev) => [
          ...prev,
          {
            parts: aiResponse.parts,
            role: aiResponse.role,
            timestamp: aiResponse.timestamp || new Date(),
          },
        ]);
        setChatId(aiResponse.id);
      } catch (error) {
        console.error('Error fetching AI response:', error);
        setMessages((prev) => [
          ...prev,
          {
            parts: [{ text: AI_ERROR_MESSAGE }],
            role: 'model',
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsProcessing(false);
      }
    },
    [chatId, fundOpportunityId, getBackendService, inputValue, isProcessing, messages],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSendMessage();
    },
    [handleSendMessage],
  );

  const handlePresetClick = useCallback(
    (prompt: string) => {
      if (isProcessing) return;
      handleSendMessage(prompt);
    },
    [handleSendMessage, isProcessing],
  );

  return {
    messages,
    isProcessing,
    addresses,
    mapBounds,
    showMap,
    hasAcceptedDisclaimer,
    isLoadingDisclaimer,
    isAcceptingDisclaimer,
    statusLoadError,
    allowedForImpersonation,
    inputValue,
    setInputValue,
    chatContainerRef,
    lastMessageSender,
    hasMessages,
    fetchDisclaimerStatus,
    handleAcceptDisclaimer,
    handleSendMessage,
    handleSubmit,
    handlePresetClick,
  };
}
